import React, {useState, useEffect, useRef} from "react";
import { 
    Text, 
    Button,
    Checkbox
} from "@fluentui/react-components";
import { useStyles } from "./TermsAndConditions.style";
import { OnboardingStepWrapper } from "./OnboardingStepWrapper";
import { OnboardingData } from "../../pages/OnboardingFlow/OnboardingFlow";
import { TERMS_AND_CONDITIONS_URL } from "../../config/externalURLs";
import useEventTracker from "../../hooks/useEventTracker";
import { ElementButton, PageName } from "../../tracking/tracking.types";


type TermsAndConditionsProps = {
    onNext: (data: Pick<OnboardingData, 'termsAndConditions'>) => void;
    onBack: () => void;
    currentStep: number;
    totalSteps: number;
}
export const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({ onNext, onBack, currentStep, totalSteps }) => {
    const styles = useStyles();
    const [accepted, setAccepted] = useState(false); //has the user accepted the terms and conditions
    const [linkClicked, setLinkClicked] = useState(false); //has the user clicked the link to view the terms and conditions

    const eventTracker = useEventTracker();
    const pageLoadStartTime = useRef<number>(performance.now());
    const hasTrackedPageLoad = useRef<boolean>(false);

    useEffect(() => {
        if (!hasTrackedPageLoad.current && eventTracker.isUsageTrackingInitialized) {
            const loadTimeMs = Math.round(performance.now() - pageLoadStartTime.current);
            eventTracker.eventTrackOnPageLoaded({
                pageName: PageName.ONBOARDING_TERMS,
                loadTimeMs,
            });
            hasTrackedPageLoad.current = true;
        }
    }, [eventTracker.isUsageTrackingInitialized]);

    const handleOnContinue = () => {
        if (accepted) {
            eventTracker.eventTrackOnElementClicked({
                pageName: PageName.ONBOARDING_TERMS,
                elementName: ElementButton.CONTINUE,
                elementType: 'button',
            });
            onNext({ termsAndConditions: { accepted } });
        }
    };

    const handleOnLinkClick = () => {
        eventTracker.eventTrackOnElementClicked({
            pageName: PageName.ONBOARDING_TERMS,
            elementName: ElementButton.VIEW_TERMS_AND_CONDITIONS,
            elementType: 'link',
        });
        setLinkClicked(true);
    }

    const handleAcceptChange = (checked: boolean) => {
        if (checked) {
            eventTracker.eventTrackOnElementClicked({
                pageName: PageName.ONBOARDING_TERMS,
                elementName: ElementButton.ACCEPT_TERMS_AND_CONDITIONS,
                elementType: 'checkbox',
            });
        }
        setAccepted(checked);
    }

    return (
        <OnboardingStepWrapper 
                title="Review Terms and Conditions"
                currentStep={currentStep}
                totalSteps={totalSteps}
            >           
            <Text data-testid="onboarding-terms-description">
                Please review and accept the Automated Domain Management (ADM) <b data-testid="onboarding-terms-text-emphasis"><a href={TERMS_AND_CONDITIONS_URL} target="_blank" rel="noopener noreferrer"  onClick={handleOnLinkClick} data-testid="onboarding-terms-link">Terms and Conditions</a></b> before continuing.
            </Text> 

            <div className={styles.checkboxSection} data-testid="onboarding-terms-checkbox-section">
                    <Checkbox
                    className={!linkClicked ? styles.disabledCheckbox : ''} //if the link has not been clicked, apply style. If it has, remove style
                    disabled={!linkClicked} 
                    data-testid="onboarding-terms-checkbox"
                    label= {
                        <span data-testid="onboarding-terms-checkbox-label"> 
                            I have read and agree to the Terms and Conditions for using the Automated Domain Management platform.
                            I understand my responsibilities regarding security group management and policy assignments
                        </span>
                    }
                    checked={accepted} 
                    onChange={(e, data) => handleAcceptChange(data.checked as boolean)}
                />
            </div>

            <div className={styles.buttonContainer} data-testid="onboarding-terms-button-container">
                <Button appearance="primary" disabled={(!accepted)} onClick={handleOnContinue} data-testid="onboarding-terms-continue">Continue</Button>
            </div>
        </OnboardingStepWrapper>    

    );
}